import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { dateDiffYears, dateTimeToS, dateToApi, dateToMMMMYYYY, dateToYYYYMM, dateUtcToS, dateUtcToyyyyMMdd } from '../../util/dateUtil';
import { getRowFromListById, isStudent, isAlumni, isAdmin } from '../../util/miscUtil';
import { appInsights, logException } from '../../services/appInsights';
import { logAudit } from '../../util/auditUtil';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import TableStyle from '../../styles/TableStyle';
import TitleBar from '../Shared/TitleBar';
import { inject, observer } from 'mobx-react';
import AddEmailDialog from './AddEmailDialog';
import PleaseWait from '../Shared/PleaseWait';
import SeniorListCards from './SeniorListCards';
import { callApi } from '../../util/apiWrapper';
import Paper from '@material-ui/core/Paper';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import FilterIcon from '@material-ui/icons/Filter';
import FilterDialog from './SeniorListDialogs/FilterDialog';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import { filterByJsonField } from '../../util/filterUtils';
import { CHECKLIST_STATUSES, PLANNING_FORM_1_STATUSES, PLANNING_FORM_2_STATUSES } from '../../constants/filterStatuses';
import { standarizeEmail } from '../../util/miscUtil';
import { useSeniorListTableStyles } from './SeniorListTable.styles';
import { fetchSeniors, makeInnerSort, createUniqueList } from './seniorListFunctions';
import PageUnauthorized from '../Shared/PageUnauthorized';

const tableStyles = makeStyles(TableStyle);

const SeniorListTable = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;

    const history = useHistory();

    const tableClasses = tableStyles();
    const classes = useSeniorListTableStyles();

    const [sortBy, setSortBy] = React.useState('');
    const [sortDirection, setSortDirection] = React.useState('asc');
    const [filter, setFilter] = React.useState('');
    const [seniors, setSeniors] = React.useState([]);
    const [showExpandedFilter, setShowExpandedFilter] = useState(false);
    const [aftercareSpecialist, setAftercareSpecialist] = useState(0);
    const [aftercareSpecialistName, setAftercareSpecialistName] = useState('');
    const [pageTitle, setPageTitle] = useState('High School Seniors');

    const EPOCH_DATE = /Date(0)/;
    const CURRENT_DATE = new Date();

    const [allSeniorsRaw, setAllSeniorsRaw] = React.useState([]);

    // Re-run effects when AuthStore hydrates
    const authReadyKey = [
      auth?.token,
      auth?.accessToken,
      auth?.jwt,
      auth?.idToken,
      auth?.user?.id,
      Array.isArray(auth?.roles) ? auth.roles.join('|') : '',
    ]
      .filter(Boolean)
      .join('|');

    const staffSitesKey = (store.staffsites ?? []).join('|');
    const staffStudentsKey = (store.staffstudents ?? []).join('|');

    // Rerun filtering when advanced filter values change
    const advancedFiltersKey = [
      store.advancedFilter,
      store.advancedFilterStatus,
      store.advancedFilterLoggedIn,
      store.advancedFilterSite,
      store.advancedFilterProgram,
      store.advancedFilterChecklist,
      store.advancedFilterPlanningForm1,
      store.advancedFilterPlanningForm2,
      store.advancedFilterScholarship,
      store.advancedFilterAftercare,
    ].join('|');

    useEffect(() => {
      const run = async () => {
        const apiSeniors = await fetchSeniors({ auth, store, roleId: 1 });
        setAllSeniorsRaw(apiSeniors);
        setSortBy('last_name');
      };

      if (auth && authReadyKey) run();
    }, [auth, authReadyKey, store]);

    useEffect(() => {
      let filteredData = Array.isArray(allSeniorsRaw) ? allSeniorsRaw : [];

      const normalizeSite = (v) => (v == null ? '' : String(v).trim().toLowerCase());
      const staffSiteSet = new Set((store.staffsites ?? []).map(normalizeSite).filter(Boolean));
      if (staffSiteSet.size > 0) {
        filteredData = filteredData.filter((s) => staffSiteSet.has(normalizeSite(s.site)));
      }

      if (Array.isArray(store.staffstudents) && store.staffstudents.length > 0) {
        const normalizeId = (id) => (id == null ? '' : String(id).trim());
        const staffStudentIds = new Set(store.staffstudents.map(normalizeId).filter(Boolean));
        filteredData = filteredData.filter((s) => staffStudentIds.has(normalizeId(s.int_id)));
      }

      // Enhanced Filter
      setPageTitle('High School Seniors');

      if (store.advancedFilterStatus === 'active') {
        filteredData = filteredData.filter((s) => s.user?.is_active);
      }
      if (store.advancedFilterStatus === 'inactive') {
        filteredData = filteredData.filter((s) => s.user?.is_active === false);
      }

      if (store.advancedFilterLoggedIn === 'loggedin') {
        filteredData = filteredData.filter((s) => s.user?.last_login);
      }
      if (store.advancedFilterLoggedIn === 'neverloggedin') {
        filteredData = filteredData.filter((s) => !s.user?.last_login);
      }

      if (store.advancedFilterSite === 'homecampus') {
        filteredData = filteredData.filter((s) => s.site === 'Home Campus');
      }
      if (store.advancedFilterSite === 'sites') {
        filteredData = filteredData.filter((s) => s.site !== 'Home Campus');
      }

      if (store.advancedFilterProgram === 'FFS') {
        filteredData = filteredData.filter((s) => s.program && s.program === 'FFS');
      }
      if (store.advancedFilterProgram === 'FHP') {
        filteredData = filteredData.filter((s) => s.program && s.program === 'FHP');
      }

      const checklistFilterMap = {
        checklistinprogress: { field: 'status', value: CHECKLIST_STATUSES.IN_PROGRESS },
        checklistsubmitted: { field: 'status', value: CHECKLIST_STATUSES.SUBMITTED },
        checklistapproved: { field: 'status', value: CHECKLIST_STATUSES.APPROVED },
      };
      const checklistFilter = checklistFilterMap[store.advancedFilterChecklist];
      if (checklistFilter) {
        filteredData = filterByJsonField(filteredData, 'checklist_data', checklistFilter);
      }

      const planningForm1FilterMap = {
        planning1inprogress: { field: 'status', value: PLANNING_FORM_1_STATUSES.IN_PROGRESS },
        planning1submitted: { field: 'status', value: PLANNING_FORM_1_STATUSES.SUBMITTED },
        planning1approved: { field: 'status', value: PLANNING_FORM_1_STATUSES.APPROVED },
        planning1followup: { field: 'followup', isTruthy: true },
      };
      const planningForm1Filter = planningForm1FilterMap[store.advancedFilterPlanningForm1];
      if (planningForm1Filter) {
        filteredData = filterByJsonField(filteredData, 'planning_form_1_data', planningForm1Filter);
      }

      const planningForm2FilterMap = {
        planning2inprogress: { field: 'status', value: PLANNING_FORM_2_STATUSES.IN_PROGRESS },
        planning2submitted: { field: 'status', value: PLANNING_FORM_2_STATUSES.SUBMITTED },
        planning2approved: { field: 'status', value: PLANNING_FORM_2_STATUSES.APPROVED },
        planning2followup: { field: 'followup', isTruthy: true },
      };
      const planningForm2Filter = planningForm2FilterMap[store.advancedFilterPlanningForm2];
      if (planningForm2Filter) {
        filteredData = filterByJsonField(filteredData, 'planning_form_2_data', planningForm2Filter);
      }

      const scholarshipFilters = {
        notstartedscholarship: (s) => !s.scholarship_app_status,
        scholarshipinprogress: (s) => s.scholarship_app_status === 'created',
        submittedscholarship: (s) => s.scholarship_app_status && s.scholarship_app_status !== 'created',
        reviewedscholarship: (s) => s.scholarship_app_status === 'reviewed',
      };

      const filterFn = scholarshipFilters[store.advancedFilterScholarship];
      if (filterFn) {
        filteredData = filteredData.filter(filterFn);
      }

      const aftercareFilters = {
        notstartedaftercareapp: (s) => !s.aftercare_app_status,
        aftercareappinprogress: (s) => s.aftercare_app_status === 'created',
        submittedaftercareapp: (s) => s.aftercare_app_status && s.aftercare_app_status !== 'created',
      };
      const aftercareFilterFn = aftercareFilters[store.advancedFilterAftercare];
      if (aftercareFilterFn) {
        filteredData = filteredData.filter(aftercareFilterFn);
      }

      if (
        store.advancedFilterStatus === '' &&
        store.advancedFilterLoggedIn === '' &&
        store.advancedFilterSite === '' &&
        store.advancedFilterProgram === '' &&
        store.advancedFilterChecklist === '' &&
        store.advancedFilterPlanningForm1 === '' &&
        store.advancedFilterPlanningForm2 === '' &&
        store.advancedFilterScholarship === '' &&
        store.advancedFilterAftercare === ''
      ) {
        store.setAdvancedFilter('all');
      }

      // Filter Field
      if (filter && filter.length > 0) {
        const filterNoSpaces = (filter || '').replace(/\s+/g, '');
        const filterLower = filterNoSpaces.toLowerCase();

        filteredData = filteredData.filter(
          (s) =>
            (s.int_id != null && s.int_id.toString().includes(filterLower)) ||
            (s.first_name && s.first_name.toLowerCase().includes(filterLower)) ||
            (s.last_name && s.last_name.toLowerCase().includes(filterLower)) ||
            (s.dob && dateUtcToS(s.dob) && dateUtcToS(s.dob).toString().includes(filterLower)) ||
            (s.graduation_date &&
              s.graduation_date > EPOCH_DATE &&
              dateToMMMMYYYY(s.graduation_date) &&
              dateToMMMMYYYY(s.graduation_date).toString().toLowerCase().includes(filterLower)) ||
            (s.graduation_date &&
              s.graduation_date > EPOCH_DATE &&
              dateToApi(s.dob) &&
              dateToApi(s.graduation_date) &&
              dateDiffYears(dateToApi(s.dob), dateToApi(s.graduation_date)) != null &&
              dateDiffYears(dateToApi(s.dob), dateToApi(s.graduation_date)).toString().includes(filterLower)) ||
            (s.site && s.site.toLowerCase().includes(filterLower)) ||
            (s.user &&
              s.user.email &&
              standarizeEmail(s.user.email) &&
              standarizeEmail(s.user.email).toString().toLowerCase().includes(filterLower)) ||
            (s.aftercare_specialist &&
              getSpecialistInfo(s.aftercare_specialist) &&
              getSpecialistInfo(s.aftercare_specialist).toString().toLowerCase().includes(filterLower)) ||
            (s.program && s.program.toLowerCase().includes(filterLower))
        );
      }

      store.setPage(0);

      if (!store.showAllRecords) {
        filteredData = filteredData.filter((x) => x.departure_date === undefined);
      }

      if (store.showNeedEmail) {
        filteredData = filteredData.filter((x) => (x.user?.id ?? 0) === 0);
      }

      store.setTotalSeniors(filteredData.length);

      filteredData = filteredData.sort(
        makeInnerSort({
          key: sortBy,
          order: sortDirection,
          standarizeEmail,
          dateToYYYYMM,
          dateUtcToyyyyMMdd,
          getSpecialistInfo,
        })
      );

      setSeniors(filteredData);
      store.setAllSeniors(filteredData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      store,
      sortBy,
      filter,
      aftercareSpecialist,
      sortDirection,
      allSeniorsRaw,
      staffSitesKey,
      staffStudentsKey,
      advancedFiltersKey,
      store.showNeedEmail,
      store.showAllRecords,
    ]);

    const setSort = (val) => {
      if (val === sortBy) {
        if (sortDirection === 'asc') {
          setSortDirection('desc');
        } else {
          setSortDirection('asc');
        }
      } else {
        setSortBy(val);
        setSortDirection('asc');
      }
    };

    const filterData = (val) => {
      setFilter(val);
    };

    const clearExpandedFilter = () => {
      store.setAdvancedFilter('');
      store.setAdvancedFilterStatus('');
      store.setAdvancedFilterLoggedIn('');
      store.setAdvancedFilterSite('');
      store.setAdvancedFilterProgram('');
      store.setAdvancedFilterChecklist('');
      store.setAdvancedFilterPlanningForm1('');
      store.setAdvancedFilterPlanningForm2('');
      store.setAdvancedFilterScholarship('');
      store.setAdvancedFilterAftercare('');
      store.setAdvancedFilter('all');
    };

    const getSpecialistInfo = (id) => {
      const spec = getRowFromListById(store.aftercareSpecialistList, id);
      if (spec) {
        return spec.user.first_name + ' ' + spec.user.last_name;
      }
      console.warn('Some seniors are assigned to invalid Successful Future specialists.');
      return '';
    };

    const handleClickId = (event, id) => {
      event.preventDefault();
      event.stopPropagation();
      store.setBasicInfoId(id);
      store.setAlumni(false);
      history.push('/seniorplanner');
    };

    const handleClickAddEmail = (event, id) => {
      event.preventDefault();
      event.stopPropagation();
      store.setAddEmailId(id);
      store.setShowAddEmail(true);
    };

    const handleEmailClose = () => {
      store.setShowAddEmail(false);
    };

    const refreshPage = () => {
      window.location.reload(true);
    };

    const handleHelpClose = () => {
      setShowExpandedFilter(false);
    };

    const handleClickResend = async (id) => {
      try {
        store.setLoading(true);
        store.setSearching(true);
        await callApi(auth, 'studentonboardemail', 'POST', { student_id: id });

        const baseList = Array.isArray(store.allseniors) ? store.allseniors.slice() : Array.isArray(allSeniorsRaw) ? [...allSeniorsRaw] : [];
        const i = baseList.findIndex((v) => v.id === id);
        if (i < 0) throw new Error(`Senior ${id} not found for resend update.`);

        const tempSenior = JSON.parse(JSON.stringify(baseList[i]));
        tempSenior.user = tempSenior.user ?? {};
        tempSenior.user.updated_at = CURRENT_DATE;
        baseList[i] = tempSenior;

        setAllSeniorsRaw((prev) => (Array.isArray(prev) ? prev.map((s) => (s?.id === id ? tempSenior : s)) : prev));

        store.setSenior(tempSenior);
        setSeniors(baseList);
        store.setSeniorData(baseList);
        store.setAllSeniors(baseList);

        store.setSearching(false);
        store.setLoading(false);
        setSort('last_name');
        setSortDirection('asc');

        appInsights.trackTrace({ message: `Invitation resent to ${id}.` });
        const auditObj = {
          audit_ts: CURRENT_DATE,
          user_id: auth.user.result.id,
          username: auth.user.result.first_name + ' ' + auth.user.result.last_name,
          audit_type: 'invitation',
          action: 'resent',
          value: `Invitation resent to ${id}.`,
        };
        logAudit(auth, auditObj);
      } catch (error) {
        logException(error);
        store.setSearching(false);
        store.setLoading(false);
        console.log('Error resending invitation:', error);
      }
    };

    const handleCardSortChange = (event) => {
      setSort(event.target.value);
    };

    const emptyRows = store.perPage - Math.min(store.perPage, seniors && seniors.length - store.page * store.perPage);

    if (auth.roles && auth.roles.length > 0 && !isAdmin(auth.roles)) {
      return <PageUnauthorized />;
    }

    return (
      <div className={classes.frame}>
        {auth.roles && auth.roles.length > 0 && isAdmin(auth.roles) && (
          <Grid container className={classes.whiteBg}>
            {auth.roles && !isStudent(auth.roles) && !isAlumni(auth.roles) && (
              <React.Fragment>
                <PleaseWait isLoading={store.loading} />
                <TitleBar
                  title={pageTitle}
                  currentPage='High School Senior List'
                  paths={[{ path: '/', name: 'Home' }]}
                  additionalInformation={aftercareSpecialistName ? `Specialist: ${aftercareSpecialistName}` : ''}
                />
                <div className={classes.frame}>
                  <div className={classes.alignPage}>
                    <Grid container className={classes.formatTableSize}>
                      <Grid item xs={12} className={classes.whiteBg} align='left'>
                        <Grid container direction='row'>
                          <Grid item xs={12} sm={12} md={4} lg={3} className={classes.whiteBg} align='left'>
                            <TextField
                              id='filter-field'
                              type='search'
                              className={classes.filter}
                              size='small'
                              hiddenLabel
                              placeholder='Filter'
                              variant='outlined'
                              onChange={(event) => filterData(event.target.value)}
                              clearable='true'
                              InputProps={{
                                endAdornment: (
                                  <>
                                    <InputAdornment position='end'>
                                      <IconButton
                                        edge='end'
                                        color='primary'
                                        variant='contained'
                                        title='Enhanced Filter'
                                        aria-describedby='Expand Filter Options'
                                        onClick={() => setShowExpandedFilter(true)}
                                      >
                                        <FilterIcon />
                                      </IconButton>
                                    </InputAdornment>
                                    <InputAdornment position='end'>
                                      <IconButton
                                        edge='end'
                                        color='primary'
                                        variant='contained'
                                        title='Clear Enhanced Filter'
                                        aria-describedby='Clear Filter Options'
                                        onClick={() => clearExpandedFilter()}
                                      >
                                        <CancelPresentationIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  </>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={7} md={4} lg={4} className={classes.whiteBg} align='left'></Grid>
                          <Grid item xs={12} sm={5} md={4} lg={5} align='right' className={classes.whiteBg}>
                            <Hidden smDown implementation='css' className={classes.toolMargins}>
                              <FormControlLabel
                                control={<Checkbox color='default' />}
                                label='Show Only Students Without Email'
                                onChange={() => store.setShowNeedEmail(!store.showNeedEmail)}
                                checked={store.showNeedEmail}
                              />
                            </Hidden>
                          </Grid>
                        </Grid>
                        <Hidden mdDown implementation='css'>
                          <TableContainer component={Paper}>
                            <Table className={tableClasses.table}>
                              <TableHead>
                                <TableRow style={{ width: '100%' }}>
                                  <Tooltip title='Click to sort by Last Name.' placement='top'>
                                    <TableCell onClick={() => setSort('last_name')} className={classes.sortHeader} style={{ width: '240px' }}>
                                      NAME (NDB ID){' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'last_name' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'last_name' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by Birth Date.' placement='top'>
                                    <TableCell onClick={() => setSort('dob')} className={classes.sortHeader} style={{ width: '90px' }}>
                                      BIRTH DATE{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'dob' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'dob' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by Graduation Date.' placement='top'>
                                    <TableCell onClick={() => setSort('graduation_date')} className={classes.sortHeader} style={{ width: '140px' }}>
                                      GRADUATION (AGE){' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'graduation_date' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'graduation_date' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  {store.showAllRecords && (
                                    <TableCell className={classes.nosortHeader} style={{ width: '90px' }}>
                                      DEPARTURE
                                    </TableCell>
                                  )}
                                  <Tooltip title='Click to sort by Site.' placement='top'>
                                    <TableCell onClick={() => setSort('site')} className={classes.sortHeader} style={{ width: '125px' }}>
                                      SITE{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'site' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'site' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by Email.' placement='top'>
                                    <TableCell onClick={() => setSort('user')} className={classes.sortHeader} style={{ width: '180px' }}>
                                      EMAIL ADDRESS{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'user' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'user' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by Specialist.' placement='top'>
                                    <TableCell
                                      onClick={() => setSort('aftercare_specialist')}
                                      className={classes.sortHeader}
                                      style={{ width: '120px' }}
                                    >
                                      SPECIALIST{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'aftercare_specialist' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'aftercare_specialist' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <Tooltip title='Click to sort by Program.' placement='top'>
                                    <TableCell onClick={() => setSort('program')} className={classes.sortHeader} style={{ width: '100px' }}>
                                      PROGRAM{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'program' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'program' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                  </Tooltip>
                                  <TableCell className={classes.nosortHeader} style={{ width: '90px' }}>
                                    RESEND INVITE
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {store.searching ? (
                                  <TableRow style={{ width: '100%' }}>
                                    <TableCell align='center' colSpan={8}>
                                      <Typography variant='h5'>Searching...</Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  seniors &&
                                  createUniqueList(seniors)
                                    .slice(store.page * store.perPage, store.page * store.perPage + store.perPage)
                                    .map((senior, idx) => (
                                      <TableRow
                                        style={{ width: '100%' }}
                                        key={senior.id + '_' + idx}
                                        className={classes.pointer}
                                        onClick={(ev) => handleClickId(ev, senior.id)}
                                      >
                                        <TableCell style={{ width: '240px' }}>
                                          {senior.last_name}, {senior.first_name} ({senior.int_id})
                                        </TableCell>
                                        <TableCell style={{ width: '90px' }}>{dateUtcToS(senior.dob)}</TableCell>
                                        <TableCell style={{ width: '140px' }}>
                                          {senior.graduation_date > EPOCH_DATE && dateToMMMMYYYY(senior.graduation_date)}
                                          {senior.graduation_date > EPOCH_DATE &&
                                            ' (' + dateDiffYears(dateToApi(senior.dob), dateToApi(senior.graduation_date)) + ')'}
                                        </TableCell>
                                        {store.showAllRecords && (
                                          <TableCell style={{ width: '90px' }}>
                                            {senior.departure_date > EPOCH_DATE && dateUtcToS(senior.departure_date)}
                                          </TableCell>
                                        )}
                                        <TableCell style={{ width: '125px' }}>{!senior.departure_date && senior.site && senior.site}</TableCell>
                                        <TableCell style={{ width: '180px' }}>
                                          {senior.departure_date && senior.user?.email && (
                                            <label className={classes.idNoLink}>{senior.user.email}</label>
                                          )}
                                          {!senior.departure_date && senior.user?.email ? (
                                            <React.Fragment>{senior.user.email}</React.Fragment>
                                          ) : (
                                            !senior.departure_date && (
                                              <React.Fragment>
                                                <Button
                                                  className={classes.gridLink}
                                                  title='Add email address for student.'
                                                  aria-describedby='Add email address for senior'
                                                  onClick={(ev) => handleClickAddEmail(ev, senior.id)}
                                                >
                                                  Add Email Address
                                                </Button>
                                              </React.Fragment>
                                            )
                                          )}
                                        </TableCell>
                                        <TableCell style={{ width: '120px' }}>
                                          {senior.aftercare_specialist > 0 ? getSpecialistInfo(senior.aftercare_specialist) : ''}
                                        </TableCell>
                                        <TableCell style={{ width: '100px' }}>{senior.program && senior.program}</TableCell>
                                        <TableCell style={{ width: '90px' }} className={classes.centerAlignContent}>
                                          {!senior.departure_date && senior.user?.created_at && (
                                            <React.Fragment>
                                              <Tooltip
                                                title={`Invited on ${
                                                  senior.user?.updated_at ? dateTimeToS(senior.user.updated_at) : ''
                                                }. Resend invitation.`}
                                                placement='left'
                                              >
                                                <IconButton
                                                  className={classes.gridLink}
                                                  name='resendInvitation'
                                                  aria-describedby='Resend invitation'
                                                  onClick={(ev) => {
                                                    ev.preventDefault();
                                                    ev.stopPropagation();
                                                    handleClickResend(senior.id);
                                                  }}
                                                >
                                                  <MailOutlineIcon color='primary' />
                                                </IconButton>
                                              </Tooltip>
                                            </React.Fragment>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))
                                )}
                                {!store.searching && seniors && seniors.length === 0 ? (
                                  <TableRow>
                                    <TableCell align='center' colSpan={8} className={classes.noLeftPadding}>
                                      <summary className={classes.noSeniorsMsg}>No records found.</summary>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <React.Fragment />
                                )}
                                {emptyRows > 0 && (
                                  <TableRow style={{ height: 34 * emptyRows }}>
                                    <TableCell colSpan={8} />
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                            <TablePagination
                              component='div'
                              colSpan={8}
                              SelectProps={{
                                InputProps: { 'aria-label': 'rows per page' },
                                MenuProps: { classes: { paper: classes.selectDropdown } },
                              }}
                              classes={{ menuItem: classes.menuItem }}
                              rowsPerPageOptions={[10, 15, 25, 50, 100, 150, 200, 250]}
                              count={store.seniorPages}
                              rowsPerPage={store.perPage}
                              page={store.page}
                              onPageChange={(ev, val) => store.handlePageChange(ev, val)}
                              onRowsPerPageChange={(ev) => store.handleChangeRowsPerPage(ev)}
                            />
                          </TableContainer>
                        </Hidden>
                        <Hidden lgUp implementation='css'>
                          <Grid container>
                            <Grid item xs={12} sm={7} md={5} className={classes.radioRow}>
                              <RadioGroup
                                aria-label='card-sorting'
                                name='date-group'
                                className={classes.centerRadioSorting}
                                row
                                onChange={handleCardSortChange}
                              >
                                <FormControlLabel
                                  checked={sortBy === 'int_id'}
                                  control={<Radio color='default' />}
                                  label={<span className={classes.radioLabel}>Sort by Id</span>}
                                  onClick={() => setSort('int_id')}
                                  className={classes.radioLabel}
                                />
                                <aside
                                  style={{
                                    marginLeft: '-15px',
                                    marginTop: '8px',
                                    marginRight: '10px',
                                  }}
                                >
                                  <span
                                    className='fas fa-caret-up'
                                    style={{
                                      display: sortBy === 'int_id' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                  <span
                                    className='fas fa-caret-down'
                                    style={{
                                      display: sortBy === 'int_id' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                </aside>
                                <FormControlLabel
                                  checked={sortBy === 'last_name'}
                                  control={<Radio color='default' />}
                                  label={<span className={classes.radioLabel}>Sort by Last Name</span>}
                                  onClick={() => setSort('last_name')}
                                  className={classes.radioLabel}
                                  style={{
                                    marginLeft: '20px',
                                  }}
                                />
                                <aside
                                  style={{
                                    marginLeft: '-15px',
                                    marginTop: '8px',
                                    marginRight: '10px',
                                  }}
                                >
                                  <span
                                    className='fas fa-caret-up'
                                    style={{
                                      display: sortBy === 'last_name' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                  <span
                                    className='fas fa-caret-down'
                                    style={{
                                      display: sortBy === 'last_name' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                    }}
                                  />
                                </aside>
                              </RadioGroup>
                            </Grid>
                          </Grid>
                          {seniors && seniors.length > 0 && (
                            <SeniorListCards
                              SeniorStore={store}
                              seniors={seniors}
                              resendEmail={handleClickResend}
                              createUniqueList={createUniqueList}
                            />
                          )}
                        </Hidden>
                      </Grid>
                    </Grid>
                  </div>
                </div>
                <AddEmailDialog SeniorStore={store} AuthStore={auth} handleClose={handleEmailClose} refresh={refreshPage} />
              </React.Fragment>
            )}
            <FilterDialog
              filterOption={store.advancedFilter}
              setFilterOption={store.setAdvancedFilter}
              filterAll={store.advancedFilter}
              setFilterAll={store.setAdvancedFilter}
              filterStatus={store.advancedFilterStatus}
              setFilterStatus={store.setAdvancedFilterStatus}
              filterLoggedIn={store.advancedFilterLoggedIn}
              setFilterLoggedIn={store.setAdvancedFilterLoggedIn}
              filterProgram={store.advancedFilterProgram}
              setFilterProgram={store.setAdvancedFilterProgram}
              filterChecklist={store.advancedFilterChecklist}
              setFilterChecklist={store.setAdvancedFilterChecklist}
              filterPlanningForm1={store.advancedFilterPlanningForm1}
              setFilterPlanningForm1={store.setAdvancedFilterPlanningForm1}
              filterPlanningForm2={store.advancedFilterPlanningForm2}
              setFilterPlanningForm2={store.setAdvancedFilterPlanningForm2}
              filterScholarship={store.advancedFilterScholarship}
              setFilterScholarship={store.setAdvancedFilterScholarship}
              filterAftercare={store.advancedFilterAftercare}
              setFilterAftercare={store.setAdvancedFilterAftercare}
              filterSite={store.advancedFilterSite}
              setFilterSite={store.setAdvancedFilterSite}
              aftercareList={store.aftercareSpecialistList}
              aftercareSpecialist={aftercareSpecialist}
              setAftercareSpecialist={setAftercareSpecialist}
              setAftercareSpecialistName={setAftercareSpecialistName}
              showDialog={showExpandedFilter}
              handleClose={handleHelpClose}
            />
          </Grid>
        )}
      </div>
    );
  })
);

export default SeniorListTable;
